import { bullionCricketFormatOversFromMatch } from "../../Cricket/cricketMatchCardOverDisplay";
import { bullionLobbyMatchIdsEqual, bullionLobbyUserHasActivePlay } from "./bullionMatchLandingLobby";
import { bullionLobbyUserHasLobbyRelevantPlay, bullionUserHasUnsettledPlayOnMatch } from "./bullionLobbyStalePrune";
import type { BullionLobbyUserActiveGame } from "./types";
import type { RawMatch } from "./bullionRawMatch";
import { resolveBullionFixtureScheduledStartMs, parseBullionFixtureDatetimeToMs } from "./bullionFixtureScheduledStart";
import { isRawMatchFinishedForBullionLobby, isRawMatchLiveForBullionLobby } from "./bullionRawMatch";
import { bullionOddsSnapshotMarkets } from "./bullionOddsSnapshot";
import { compareMatchUpcomingPhase } from "../../../utils/matchTimeHorizon";

function bullionMatchStartMs(match: RawMatch): number | null {
    const m = match as Record<string, unknown>;
    const raw = m.actual_start ?? match.start ?? m["starts-at"] ?? m.startTime ?? m.start_time;
    return parseBullionFixtureDatetimeToMs(raw);
}

/** Scheduled wall-clock start only — `actual_start` can move early during toss feed updates. */
function bullionMatchScheduledStartMs(match: RawMatch): number | null {
    return resolveBullionFixtureScheduledStartMs(match);
}

/** True when the fixture is T20 (20 overs). */
export function isT20BullionLobbyMatch(match: RawMatch): boolean {
    return bullionCricketFormatOversFromMatch(match as Record<string, unknown>) === 20;
}

/** True when the fixture is F50 / ODI (50 overs). */
export function isF50BullionLobbyMatch(match: RawMatch): boolean {
    return bullionCricketFormatOversFromMatch(match as Record<string, unknown>) === 50;
}

/** True when the fixture is The Hundred (Decimal format 100). */
export function isHundredBullionLobbyMatch(match: RawMatch): boolean {
    return bullionCricketFormatOversFromMatch(match as Record<string, unknown>) === 100;
}

function matchHasBullionLobbyFrozenBoardFromApi(match: RawMatch): boolean {
    const m = match as Record<string, unknown>;
    const snap = m.opening_odds ?? m.openingOdds;
    if (bullionOddsSnapshotMarkets(snap).length > 1) return true;
    return bullionOddsSnapshotMarkets(m.markets).length > 1;
}

/** Fixtures offered in the Bullion lobby (keep aligned with `isBullionOfferMatch` in backend). */
export function isBullionLobbyOfferMatch(match: RawMatch): boolean {
    if (matchHasBullionLobbyFrozenBoardFromApi(match)) return true;
    const overs = bullionCricketFormatOversFromMatch(match as Record<string, unknown>);
    return overs === 20 || overs === 50 || overs === 100;
}

/**
 * When `/bullion/lobby-matches` refreshes it omits live fixtures; keep the last known row for each
 * active-play match id that disappeared from the new API payload.
 */
export function pickPreservedLobbyRowsForActivePlays(
    prevRows: RawMatch[],
    apiRows: RawMatch[],
    activeMids: string[],
    activeByMatch?: Record<string, BullionLobbyUserActiveGame[]>
): RawMatch[] {
    if (activeMids.length === 0) return [];
    const preserved: RawMatch[] = [];
    for (const want of activeMids) {
        const id = String(want || "").trim();
        if (!id) continue;
        if (activeByMatch && !bullionUserHasUnsettledPlayOnMatch(id, activeByMatch)) continue;
        if (apiRows.some((m) => bullionLobbyMatchIdsEqual(String(m.id ?? ""), id))) continue;
        const fromPrev = prevRows.find((m) => bullionLobbyMatchIdsEqual(String(m.id ?? ""), id));
        if (fromPrev && !isRawMatchFinishedForBullionLobby(fromPrev)) preserved.push(fromPrev);
    }
    return preserved;
}

function normalizeBullionLobbyTeamToken(raw: unknown): string {
    return String(raw ?? "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
}

/**
 * Stable key for the same real-world fixture listed under multiple Decimal match ids
 * (e.g. LIVE2026 in-play + LIVE206 still "Match Scheduled").
 */
export function bullionLobbyFixtureDedupeKey(match: RawMatch): string | null {
    const m = match as Record<string, unknown>;
    const home = normalizeBullionLobbyTeamToken(m.home);
    const away = normalizeBullionLobbyTeamToken(m.away);
    let teams: string[];
    if (home && away) {
        teams = [home, away].sort();
    } else {
        const name = String(m.name ?? m.matchName ?? "").trim();
        if (!name) return null;
        const beforeComma = name.split(",")[0] || name;
        const parts = beforeComma
            .split(/\s+v(?:s)?\.?\s+/i)
            .map((t) => normalizeBullionLobbyTeamToken(t))
            .filter(Boolean);
        if (parts.length < 2) return null;
        teams = parts.slice(0, 2).sort();
    }
    const startMs = bullionMatchScheduledStartMs(match) ?? bullionMatchStartMs(match);
    if (startMs == null) return null;
    const startBucket = Math.floor(startMs / 60000);
    // Teams + kickoff only — DB-merged rows often omit competition/home/away.
    return `${teams[0]}\u001f${teams[1]}\u001f${startBucket}`;
}

function bullionTwinReadinessScore(match: RawMatch, isLive: (m: RawMatch) => boolean): number {
    const status = String(match.status || "").toUpperCase();
    const id = String(match.id || "");
    if (isRawMatchFinishedForBullionLobby(match)) return -100;
    let score = 10;
    if (isLive(match)) score = 100;
    else if (status.includes("PLAY TO START SHORTLY") || status.includes("MATCH STABLE")) score = 60;
    else if (status.includes("TOSS")) score = 45;
    else if (status.includes("MATCH SCHEDULED") || status.includes("PRE MATCH")) score = 30;
    // Prefer …LIVE2026; demote truncated …LIVE206 twins (not the bookable fixture).
    if (/LIVE\d{4}$/i.test(id)) score += 10;
    else if (/LIVE\d{3}$/i.test(id)) score -= 10;
    return score;
}

/**
 * Collapse Decimal duplicate listings of the same fixture (same teams + kickoff + competition).
 * Prefer rows the user already entered; otherwise prefer live / nearer-to-start over stale scheduled twins.
 */
export function collapseDuplicateBullionLobbyFixtures(
    matches: RawMatch[],
    opts?: {
        hasUserPlay?: (matchId: string) => boolean;
        isLive?: (match: RawMatch) => boolean;
    }
): RawMatch[] {
    const list = Array.isArray(matches) ? matches : [];
    if (list.length < 2) return list;

    const hasUserPlay = opts?.hasUserPlay ?? (() => false);
    const isLive = opts?.isLive ?? ((m: RawMatch) => isRawMatchLiveForBullionLobby(m));

    const groups = new Map<string, RawMatch[]>();
    const order: string[] = [];
    for (const m of list) {
        const key = bullionLobbyFixtureDedupeKey(m) || `\0id:${String(m.id ?? "")}`;
        if (!groups.has(key)) {
            groups.set(key, []);
            order.push(key);
        }
        groups.get(key)!.push(m);
    }

    const out: RawMatch[] = [];
    for (const key of order) {
        const group = groups.get(key) || [];
        if (group.length <= 1 || key.startsWith("\0id:")) {
            out.push(...group);
            continue;
        }

        const withPlay: RawMatch[] = [];
        const withoutPlay: RawMatch[] = [];
        for (const m of group) {
            const id = String(m.id ?? "").trim();
            if (id && hasUserPlay(id)) withPlay.push(m);
            else withoutPlay.push(m);
        }

        if (withPlay.length > 0) {
            out.push(...withPlay);
            continue;
        }

        let best = withoutPlay[0];
        let bestScore = bullionTwinReadinessScore(best!, isLive);
        for (let i = 1; i < withoutPlay.length; i++) {
            const cand = withoutPlay[i]!;
            const score = bullionTwinReadinessScore(cand, isLive);
            if (score > bestScore) {
                best = cand;
                bestScore = score;
            }
        }
        if (best) out.push(best);
    }
    return out;
}

/** Merge Bullion lobby sources (dedupe by match id; primary order wins). */
export function mergeBullionLobbySourceLists(primary: RawMatch[], cricketSupplement: RawMatch[]): RawMatch[] {
    const seen = new Set<string>();
    const out: RawMatch[] = [];
    for (const m of primary) {
        const id = String(m.id ?? "").trim();
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push(m);
    }
    for (const m of cricketSupplement) {
        const id = String(m.id ?? "").trim();
        if (!id || seen.has(id)) continue;
        if (String(m.sport || "").toLowerCase() !== "cricket") continue;
        seen.add(id);
        out.push(m);
    }
    return out;
}

export type BullionLobbySelectOpts = {
    userActiveMatchIds: Set<string>;
    daysAhead: number;
    maxMatches: number;
    bullionActivePlaysLoaded: boolean;
    activeBullionGamesByMatchId?: Record<string, BullionLobbyUserActiveGame[]>;
};

function shouldIncludeBullionLobbyOfferMatch(
    match: RawMatch,
    userActiveMatchIds: Set<string>,
    bullionActivePlaysLoaded: boolean
): boolean {
    if (bullionActivePlaysLoaded) {
        const id = String(match.id ?? "").trim();
        if (id && bullionLobbyUserHasActivePlay(id, userActiveMatchIds)) return true;
    }
    return isBullionLobbyOfferMatch(match);
}

/**
 * Bullion lobby: in-progress fixtures only when the user has an active play, except pre-kickoff
 * toss/build-up where the stream can report live before scheduled start; upcoming within `daysAhead`;
 * T20/F50 offers only; ordered by time horizon → competition priority → start time (same as backend).
 */
export function selectAndOrderBullionLobbyMatches(matches: RawMatch[], opts: BullionLobbySelectOpts): RawMatch[] {
    const { userActiveMatchIds, daysAhead, maxMatches, bullionActivePlaysLoaded, activeBullionGamesByMatchId } =
        opts;
    const now = Date.now();
    const windowEnd = now + daysAhead * 86400000;
    const windowStart = now - 12 * 3600000;

    const filtered: RawMatch[] = [];
    const filteredIds = new Set<string>();

    const push = (row: RawMatch) => {
        const rid = String(row.id ?? "").trim();
        if (!rid || filteredIds.has(rid)) return;
        filteredIds.add(rid);
        filtered.push(row);
    };

    const inUpcomingWindow = (start: number | null) =>
        start != null && start <= windowEnd && start >= windowStart;

    for (const m of matches) {
        if (String(m.sport || "").toLowerCase() !== "cricket") continue;
        const id = String(m.id ?? "").trim();
        if (!id) continue;
        const live = isRawMatchLiveForBullionLobby(m);
        const startMs = bullionMatchStartMs(m);
        const scheduledStartMs = bullionMatchScheduledStartMs(m);
        const userPlay = bullionLobbyUserHasLobbyRelevantPlay(
            id,
            userActiveMatchIds,
            bullionActivePlaysLoaded,
            activeBullionGamesByMatchId
        );

        if (bullionActivePlaysLoaded && userPlay) {
            push(m);
            continue;
        }

        if (live) {
            if (!bullionActivePlaysLoaded && inUpcomingWindow(startMs ?? scheduledStartMs)) {
                if (shouldIncludeBullionLobbyOfferMatch(m, userActiveMatchIds, bullionActivePlaysLoaded)) push(m);
                continue;
            }
            if (
                scheduledStartMs != null &&
                now < scheduledStartMs &&
                inUpcomingWindow(scheduledStartMs)
            ) {
                if (shouldIncludeBullionLobbyOfferMatch(m, userActiveMatchIds, bullionActivePlaysLoaded)) push(m);
            }
            continue;
        }

        const upcomingStartMs = scheduledStartMs ?? startMs;
        if (
            inUpcomingWindow(upcomingStartMs) &&
            shouldIncludeBullionLobbyOfferMatch(m, userActiveMatchIds, bullionActivePlaysLoaded)
        ) {
            push(m);
        }
    }

    if (bullionActivePlaysLoaded) {
        for (const m of matches) {
            const id = String(m.id ?? "").trim();
            if (!id || filteredIds.has(id)) continue;
            if (String(m.sport || "").toLowerCase() !== "cricket") continue;
            if (
                bullionLobbyUserHasLobbyRelevantPlay(
                    id,
                    userActiveMatchIds,
                    bullionActivePlaysLoaded,
                    activeBullionGamesByMatchId
                )
            ) {
                push(m);
            }
        }
    }

    const collapsed = collapseDuplicateBullionLobbyFixtures(filtered, {
        hasUserPlay: (id) =>
            bullionLobbyUserHasLobbyRelevantPlay(
                id,
                userActiveMatchIds,
                bullionActivePlaysLoaded,
                activeBullionGamesByMatchId
            ),
        isLive: isRawMatchLiveForBullionLobby,
    });

    const userPlayRows: RawMatch[] = [];
    const otherRows: RawMatch[] = [];
    for (const row of collapsed) {
        const id = String(row.id ?? "").trim();
        if (
            bullionActivePlaysLoaded &&
            id &&
            bullionLobbyUserHasLobbyRelevantPlay(
                id,
                userActiveMatchIds,
                bullionActivePlaysLoaded,
                activeBullionGamesByMatchId
            )
        ) {
            userPlayRows.push(row);
        } else {
            otherRows.push(row);
        }
    }

    const resolveLobbyPriority = (match: RawMatch): number => {
        const row = match as Record<string, unknown>;
        if (typeof row._priority === "number") return row._priority;
        return 99;
    };

    const sortRows = (rows: RawMatch[]) => {
        rows.sort((a, b) => {
            const ta = bullionMatchStartMs(a);
            const tb = bullionMatchStartMs(b);
            const phase = compareMatchUpcomingPhase(ta, tb);
            if (phase !== 0) return phase;
            const prioA = resolveLobbyPriority(a);
            const prioB = resolveLobbyPriority(b);
            if (prioA !== prioB) return prioA - prioB;
            if (ta != null && tb != null && ta !== tb) return ta - tb;
            if (ta == null && tb != null) return 1;
            if (ta != null && tb == null) return -1;
            return 0;
        });
    };

    sortRows(userPlayRows);
    sortRows(otherRows);

    const capOthers = Math.max(0, maxMatches - userPlayRows.length);
    const merged = [...userPlayRows, ...otherRows.slice(0, capOthers)];
    return merged.filter((m) => {
        if (!isRawMatchFinishedForBullionLobby(m)) return true;
        const id = String(m.id ?? "").trim();
        if (!id || !bullionActivePlaysLoaded) return false;
        return bullionUserHasUnsettledPlayOnMatch(id, activeBullionGamesByMatchId);
    });
}
